import { Plus, Home, ShoppingBag, Gift, CheckCircle2, Settings, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { FlowStep, StepType, Storefront } from '../types';
import { StepBoxWithVariants } from './StepBoxWithVariants';

interface FunnelStepColumnProps {
  colIndex: number;
  stepsInCol: FlowStep[];
  funnel: Storefront;
  onFunnelChange: (funnel: Storefront) => void;
  selectedStepId: string | null;
  onStepSelect: (stepId: string) => void;
  onStepDelete: (stepId: string) => void;
  onAddConditionalStep: (col: number) => void;
  onTransformToSplit: (stepId: string) => void;
  onEditConfig: (stepId: string) => void;
  stepRefsMap: React.MutableRefObject<Map<string, HTMLDivElement>>;
  urlViewMode: 'staging' | 'production';
  stepAnalytics?: Record<
    string,
    {
      visitors: number;
      conversions: number;
      progressionRate: number | null;
      progressedToNext: number;
      realtimeVisitors: number;
      conversionValue: number;
      conversionValueUnit: string;
      avgConversionValue: number;
      hasNextStep: boolean;
    }
  >;
  variantAnalytics?: Record<
    string,
    {
      visitors: number;
      conversions: number;
      progressionRate: number | null;
      progressedToNext: number;
      realtimeVisitors: number;
      conversionValue: number;
      conversionValueUnit: string;
      avgConversionValue: number;
      hasNextStep: boolean;
    }
  >;
}

export function FunnelStepColumn({
  colIndex,
  stepsInCol,
  funnel,
  onFunnelChange,
  selectedStepId,
  onStepSelect,
  onStepDelete,
  onAddConditionalStep,
  onTransformToSplit,
  onEditConfig,
  stepRefsMap,
  urlViewMode,
  stepAnalytics,
  variantAnalytics,
}: FunnelStepColumnProps) {
  const { t } = useTranslation('storefront');
  const firstStep = stepsInCol[0];
  const sharedType = firstStep?.type || 'landing';

  return (
    <div className="flex gap-4">
      <div className="flex w-[400px] flex-shrink-0 flex-col gap-5 pb-12">
        {/* Column header */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between text-sm font-semibold">
            <div className="flex items-center gap-2.5">
              <span className="from-primary to-primary/90 text-primary-foreground shadow-primary/30 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br text-sm font-bold shadow-lg">
                {colIndex + 1}
              </span>
              <span className="text-slate-900 dark:text-slate-100">{t('stepColumn.step', { number: colIndex + 1 })}</span>
            </div>
            {stepsInCol.length > 1 && (
              <Badge variant="outline" className="h-6 border-slate-300 px-2.5 text-xs font-medium shadow-sm">
                {t('stepColumn.alternatives', { count: stepsInCol.length })}
              </Badge>
            )}
          </div>

          {/* Step Type Selector */}
          {firstStep && (
            <Select
              value={sharedType}
              onValueChange={(newType: StepType) => {
                // Update all steps in this column to the new type
                const updatedSteps = funnel.steps.map((step) =>
                  step.position.col === colIndex ? { ...step, type: newType } : step,
                );
                onFunnelChange({ ...funnel, steps: updatedSteps });
                toast.success(t('stepColumn.pageTypeChanged', { type: newType }));
              }}
            >
              <SelectTrigger className="h-10 w-full border-slate-300 bg-white shadow-sm transition-all hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
                <SelectValue placeholder={t('stepColumn.selectPageType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="landing">
                  <span className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    {t('stepColumn.types.landing')}
                  </span>
                </SelectItem>
                <SelectItem value="checkout">
                  <span className="flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4" />
                    {t('stepColumn.types.checkout')}
                  </span>
                </SelectItem>
                <SelectItem value="offer">
                  <span className="flex items-center gap-2">
                    <Gift className="h-4 w-4" />
                    {t('stepColumn.types.offer')}
                  </span>
                </SelectItem>
                <SelectItem value="thankyou">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('stepColumn.types.thankYou')}
                  </span>
                </SelectItem>
                <SelectItem value="custom">
                  <span className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    {t('stepColumn.types.custom')}
                  </span>
                </SelectItem>
                <SelectItem value="external">
                  <span className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4" />
                    {t('stepColumn.types.external')}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Steps in this column */}
        {stepsInCol.map((step) => (
          <div
            key={step.id}
            ref={(el) => {
              if (el) {
                stepRefsMap.current.set(step.id, el);
              } else {
                stepRefsMap.current.delete(step.id);
              }
            }}
          >
            <StepBoxWithVariants
              step={step}
              isSelected={selectedStepId === step.id}
              onClick={() => onStepSelect(step.id)}
              onDelete={() => onStepDelete(step.id)}
              onTransformToSplit={() => onTransformToSplit(step.id)}
              onEdit={() => onEditConfig(step.id)}
              productionDomains={funnel.productionDomains}
              urlViewMode={urlViewMode}
              productionConfig={funnel.productionConfig}
              stepAnalytics={stepAnalytics?.[step.id]}
              variantAnalytics={variantAnalytics}
            />
          </div>
        ))}

        {/* Add alternative version button */}
        <button
          onClick={() => onAddConditionalStep(colIndex)}
          className="group/add flex h-28 w-full cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white/50 shadow-sm transition-all hover:scale-[1.02] hover:border-purple-400 hover:bg-purple-50 dark:border-slate-700 dark:bg-slate-900/50 dark:hover:border-purple-600 dark:hover:bg-purple-950"
        >
          <div className="flex flex-col items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-dashed border-slate-300 transition-all group-hover/add:scale-110 group-hover/add:border-purple-400 group-hover/add:bg-purple-100 dark:border-slate-600 dark:group-hover/add:border-purple-600 dark:group-hover/add:bg-purple-950">
              <Plus className="h-5 w-5 text-slate-400 transition-colors group-hover/add:text-purple-600 dark:text-slate-500 dark:group-hover/add:text-purple-400" />
            </div>
            <span className="text-xs font-semibold text-slate-500 transition-colors group-hover/add:text-purple-700 dark:text-slate-400 dark:group-hover/add:text-purple-300">
              {t('stepColumn.addAlternative')}
            </span>
          </div>
        </button>
      </div>
    </div>
  );
}
